import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table"
import { useEffect, useState, useMemo, useCallback } from "react"
import api from "../../services/api"
import AdminLayout from "../../Components/Layout/AdminLayout"
import Button from "../../Components/Button"
import FAQFormModal from "../../Components/Modals/FAQFormModal"
import { FAQ_CATEGORIES, FAQ_AUDIENCES } from "../../constants/faqConstants"

const col = createColumnHelper()

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState("")
  const [catFilter, setCatFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [audFilter, setAudFilter] = useState("all")
  const [sorting, setSorting] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingFaq, setEditingFaq] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => { loadFaqs() }, [])

  const loadFaqs = () => {
    setLoading(true)
    api.getAdminFAQs()
      .then(res => { setFaqs(res.data); setLoading(false) })
      .catch(() => { setFaqs([]); setLoading(false) })
  }

  const counts = useMemo(() => ({
    total: faqs.length,
    published: faqs.filter(f => f.status === "published").length,
    draft: faqs.filter(f => f.status !== "published").length,
    featured: faqs.filter(f => f.featured).length,
  }), [faqs])

  const handleToggleFeatured = useCallback(async (faq, e) => {
    e.stopPropagation()
    await api.updateFAQ(faq.id, { featured: !faq.featured }).catch(() => {})
    loadFaqs()
  }, [])

  const handleToggleStatus = useCallback(async (faq, e) => {
    e.stopPropagation()
    await api.updateFAQ(faq.id, { status: faq.status === "published" ? "draft" : "published" }).catch(() => {})
    loadFaqs()
  }, [])

  const handleDelete = async () => {
    setDeleting(true)
    await api.deleteFAQ(deleteId).catch(() => {})
    setDeleting(false)
    setDeleteId(null)
    loadFaqs()
  }

  const handleExportCsv = () => {
    const headers = ["Question", "Category", "Audiences", "Keywords", "Status", "Visibility", "Updated"]
    const rows = filteredData.map(f => [
      `"${(f.question || "").replace(/"/g, '""')}"`,
      f.category,
      (f.audiences || []).join("; "),
      (f.keywords || []).join("; "),
      f.status,
      f.visible_unauth ? "Public" : "Auth only",
      f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "",
    ])
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `faqs-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }

  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    return faqs.filter(f => {
      if (catFilter !== "all" && f.category !== catFilter) return false
      if (statusFilter !== "all" && f.status !== statusFilter) return false
      if (audFilter !== "all" && !(f.audiences || []).includes(audFilter)) return false
      if (!q) return true
      return [f.question, f.answer, ...(f.keywords || [])].join(" ").toLowerCase().includes(q)
    })
  }, [faqs, searchText, catFilter, statusFilter, audFilter])

  const columns = useMemo(() => [
    col.accessor("featured", {
      header: "",
      size: 44,
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={e => handleToggleFeatured(row.original, e)}
          title={row.original.featured ? "Featured" : "Mark featured"}
          className={`w-7 h-7 flex items-center justify-center rounded transition-colors hover:bg-gray-100 ${row.original.featured ? "text-brand" : "text-gray-300 hover:text-gray-400"}`}
        >
          <i className={`${row.original.featured ? "fa-solid" : "fa-regular"} fa-star text-sm`} />
        </button>
      ),
    }),
    col.accessor("question", {
      header: "Question",
      size: 320,
      cell: ({ row }) => {
        const keywords = (row.original.keywords || []).slice(0, 4)
        const extra = (row.original.keywords || []).length - 4
        return (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {row.original.question || <em className="text-gray-400 font-normal">(untitled)</em>}
            </p>
            {keywords.length > 0 && (
              <div className="flex items-center gap-1 mt-1">
                {keywords.map(k => (
                  <span key={k} className="inline-block bg-gray-100 text-gray-500 text-[10px] font-medium px-1.5 py-px rounded whitespace-nowrap">{k}</span>
                ))}
                {extra > 0 && <span className="text-[10px] text-gray-400">+{extra}</span>}
              </div>
            )}
          </div>
        )
      },
    }),
    col.accessor("category", {
      header: "Category",
      size: 160,
      cell: ({ getValue }) => {
        const cat = FAQ_CATEGORIES.find(c => c.id === getValue())
        if (!cat) return <span className="text-gray-400">—</span>
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: cat.hex }}>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cat.hex }} />
            {cat.label}
          </span>
        )
      },
    }),
    col.accessor("audiences", {
      header: "Audiences",
      size: 140,
      cell: ({ getValue }) => {
        const auds = (getValue() || []).map(id => FAQ_AUDIENCES.find(a => a.id === id)?.label).filter(Boolean)
        if (!auds.length) return <span className="text-gray-400 text-xs">—</span>
        return (
          <div className="flex flex-wrap gap-1">
            {auds.slice(0, 2).map(l => (
              <span key={l} className="bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-full">{l}</span>
            ))}
            {auds.length > 2 && <span className="text-[10px] text-gray-400 self-center">+{auds.length - 2}</span>}
          </div>
        )
      },
    }),
    col.accessor("status", {
      header: "Status",
      size: 120,
      cell: ({ row }) => (
        <button
          onClick={e => handleToggleStatus(row.original, e)}
          title={row.original.status === "published" ? "Click to unpublish" : "Click to publish"}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border transition-colors ${
            row.original.status === "published"
              ? "bg-green-50 border-green-400 text-green-700 hover:bg-green-100"
              : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${row.original.status === "published" ? "bg-green-500" : "bg-gray-300"}`} />
          {row.original.status === "published" ? "Published" : "Draft"}
        </button>
      ),
    }),
    col.accessor("visible_unauth", {
      header: "Visibility",
      size: 110,
      cell: ({ getValue }) => (
        <span className={`inline-flex items-center gap-1.5 text-xs ${getValue() ? "text-gray-600" : "text-gray-400"}`}>
          <i className={`fa-solid ${getValue() ? "fa-globe" : "fa-lock"} text-xs`} />
          {getValue() ? "Public" : "Auth only"}
        </span>
      ),
    }),
    col.accessor("updated_at", {
      header: "Updated",
      size: 120,
      cell: ({ getValue }) => (
        <span className="text-xs text-gray-400">
          {getValue() ? new Date(getValue()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
        </span>
      ),
    }),
    col.display({
      id: "actions",
      header: "",
      size: 80,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={e => { e.stopPropagation(); setEditingFaq(row.original); setModalOpen(true) }}
            className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-50 text-xs transition-colors"
            title="Edit"
          >
            <i className="fa-solid fa-pen" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); setDeleteId(row.original.id) }}
            className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-md text-red-300 hover:text-red-600 hover:bg-red-50 text-xs transition-colors"
            title="Delete"
          >
            <i className="fa-solid fa-trash" />
          </button>
        </div>
      ),
    }),
  ], [handleToggleFeatured, handleToggleStatus])

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const deletingFaq = faqs.find(f => f.id === deleteId)

  return (
    <AdminLayout
      headerActions={
        <a
          href="/faq"
          target="_blank"
          rel="noreferrer"
          className="text-sm font-semibold text-brandSecondary hover:underline flex items-center gap-1.5"
        >
          <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
          View public FAQ page
        </a>
      }
    >
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Title row */}
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-brand leading-tight">FAQs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage the public help center.{" "}
              <strong className="text-gray-700">{counts.published}</strong> published ·{" "}
              <strong className="text-gray-700">{counts.draft}</strong> draft ·{" "}
              <strong className="text-gray-700">{counts.featured}</strong> featured
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary-outline" onClick={handleExportCsv}>
              <i className="fa-solid fa-arrow-down-to-line mr-2" />
              Export CSV
            </Button>
            <Button variant="default" onClick={() => { setEditingFaq(null); setModalOpen(true) }}>
              <i className="fa-solid fa-plus mr-2" />
              Add FAQ
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-4 gap-2 bg-white border border-gray-100 rounded-xl p-3 mb-3 shadow-sm">
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search question, answer, keywords…"
              className="w-full bg-gray-50 border-0 rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="bg-gray-50 border-0 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:ring-2 focus:ring-brand/30 cursor-pointer">
            <option value="all">All categories</option>
            {FAQ_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-gray-50 border-0 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:ring-2 focus:ring-brand/30 cursor-pointer">
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <select value={audFilter} onChange={e => setAudFilter(e.target.value)} className="bg-gray-50 border-0 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:ring-2 focus:ring-brand/30 cursor-pointer">
            <option value="all">All audiences</option>
            {FAQ_AUDIENCES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>

        {/* Results bar */}
        <div className="flex justify-between items-center mb-2 px-1 text-xs text-gray-400">
          <span>Showing <strong className="text-gray-600">{filteredData.length}</strong> of {counts.total}</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-gray-100 bg-gray-50">
                  {hg.headers.map(header => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 select-none"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? "flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors" : ""}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="text-gray-300">
                              {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? "↕"}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="py-16 text-center text-gray-400 text-sm">
                    <i className="fa fa-spinner fa-spin mr-2" />Loading…
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-16 text-center">
                    <i className="fa-regular fa-folder-open text-3xl text-gray-200 mb-3 block" />
                    <p className="font-semibold text-gray-500 text-sm">No FAQs match these filters</p>
                    <p className="text-xs text-gray-400 mt-1">Adjust the search or filters above.</p>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => { setEditingFaq(row.original); setModalOpen(true) }}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{ width: cell.column.getSize() }} className="px-4 py-3 align-middle">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FAQFormModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingFaq(null) }}
        onSuccess={loadFaqs}
        faq={editingFaq}
        allFaqs={faqs}
      />

      {/* Delete confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-triangle-exclamation text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete this FAQ?</h3>
                <p className="text-sm text-gray-500">This can't be undone.</p>
              </div>
            </div>
            {deletingFaq && (
              <div className="bg-gray-50 rounded-lg p-3 mb-5 text-sm text-gray-700">
                "{deletingFaq.question}"
              </div>
            )}
            <div className="flex justify-between gap-3">
              <Button variant="secondary-ghost" onClick={() => setDeleteId(null)} disabled={deleting}>Cancel</Button>
              <Button variant="default" onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                {deleting ? <><i className="fa fa-spinner fa-spin mr-2" />Deleting…</> : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminFAQ
